import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, CheckCircle2, XCircle, Landmark, ArrowUpDown, AlertTriangle, Clock } from 'lucide-react';
import SaakhSetuLoader from '@/components/SaakhSetuLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CBS_FIELDS = [
  { key: 'sanctioned_limit', label: 'Sanctioned Limit', format: 'currency' },
  { key: 'outstanding_amount', label: 'Outstanding Amount', format: 'currency' },
  { key: 'utilization_pct', label: 'Utilization', format: 'percent', helper: 'Higher KCC utilization indicates active farm engagement (agri-lending context)' },
  { key: 'cumulative_withdrawal_amount', label: 'Cumulative Withdrawal Amount', format: 'currency' },
  { key: 'cumulative_repayment_amount', label: 'Cumulative Repayment Amount', format: 'currency' },
  { key: 'full_repayment_flag', label: 'Full Repayment', format: 'boolean' },
  { key: 'transaction_count_12m', label: 'Transactions (12m)', format: 'number' },
  { key: 'no_of_defaults', label: 'No. of Defaults', format: 'number' },
  { key: 'penal_interest_flag', label: 'Penal Interest', format: 'flag' },
  { key: 'account_relationship_years', label: 'Relationship (Years)', format: 'number' },
];

const RepaymentRefresh = ({ assessment, refreshAssessment }) => {
  const [loading, setLoading] = useState(false);
  const cbsData = assessment?.cbs_refresh_data;

  const handleRefresh = async () => {
    if (!assessment?.id) return;
    setLoading(true);
    try {
      await axios.post(`${API}/assessments/${assessment.id}/refresh-cbs`);
      // Refresh readiness status after CBS refresh to update score_readiness_status
      await axios.post(`${API}/assessments/${assessment.id}/refresh-readiness`);
      await refreshAssessment();
      toast.success('CBS data refreshed');
    } catch {
      toast.error('Failed to refresh CBS data');
    } finally {
      setLoading(false);
    }
  };

  const formatVal = (val, format) => {
    if (val === null || val === undefined) return 'N/A';
    switch (format) {
      case 'currency': return `Rs. ${Number(val).toLocaleString()}`;
      case 'percent': return `${val}%`;
      case 'boolean': return val ? 'Yes' : 'No';
      case 'flag': return val ? 'Yes' : 'No';
      case 'number': return Number(val).toLocaleString();
      default: return String(val);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1" data-testid="repayment-refresh-title">
          Repayment & Relationship Refresh
        </h3>
        <p className="text-sm text-gray-500">
          Refresh banking relationship data from CBS (Core Banking System).
          This is a simulated refresh for the demo.
        </p>
      </div>

      {/* Refresh Button */}
      <div className="flex items-center gap-3">
        <Button
          onClick={handleRefresh}
          disabled={loading}
          variant={cbsData ? 'outline' : 'default'}
          style={!cbsData ? { background: '#2563eb' } : {}}
          data-testid="refresh-cbs-btn"
        >
          {loading ? (
            <SaakhSetuLoader size={18} />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {cbsData ? 'Re-Refresh CBS Data' : 'Refresh CBS Data'}
        </Button>
        {cbsData?.last_refreshed_at && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Clock className="h-3 w-3" />
            Last refreshed: {new Date(cbsData.last_refreshed_at).toLocaleString()}
          </div>
        )}
      </div>

      {/* CBS Data Card */}
      {cbsData ? (
        <Card className="border-green-200" data-testid="cbs-data-card">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-4">
              <Landmark className="h-4 w-4 text-green-700" />
              <span className="font-semibold text-sm text-green-800">CBS Data</span>
              <Badge className="text-[10px] bg-green-100 text-green-800 border-green-200">Refreshed</Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CBS_FIELDS.map(({ key, label, format }) => {
                const val = cbsData[key];
                const isAlert = (key === 'default_event_flag' || key === 'penal_interest_flag') && val === true;

                return (
                  <div key={key} className={`flex justify-between items-center p-2.5 rounded-md ${isAlert ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                    <span className="text-xs text-gray-600">{label}</span>
                    <span className={`text-xs font-semibold flex items-center gap-1 ${isAlert ? 'text-red-700' : 'text-gray-900'}`}>
                      {isAlert && <AlertTriangle className="h-3 w-3" />}
                      {format === 'flag' ? (
                        val ? <XCircle className="h-3.5 w-3.5 text-red-600" /> : <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                      ) : null}
                      {format !== 'flag' && formatVal(val, format)}
                      {format === 'flag' && <span className="ml-1">{val ? 'Detected' : 'None'}</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="text-center py-8 text-gray-400 border border-dashed border-gray-300 rounded-lg">
          <ArrowUpDown className="h-8 w-8 mx-auto mb-2 text-gray-300" />
          <div className="text-sm">Click "Refresh CBS Data" to simulate a banking data pull</div>
        </div>
      )}
    </div>
  );
};

export default RepaymentRefresh;
